import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Heart, BookOpen, Users, Sparkles } from 'lucide-react';
import PageHeader from '../../components/common/PageHeader';
import { schoolDivisions } from '../../data/schoolInfo';

const MontessoriOverview = () => {
  const school = schoolDivisions.montessori;

  const programs = [
    { name: 'Toddler Program', ages: '18 months - 3 years', description: 'Early introduction to learning through play and sensory experiences.' },
    { name: 'Nursery', ages: '3 - 4 years', description: 'Foundation skills development in a nurturing environment.' },
    { name: 'Kindergarten', ages: '4 - 5 years', description: 'Preparation for primary school through structured learning activities.' },
    { name: 'Primary School', ages: '6 - 11 years', description: 'Comprehensive primary education following Nigerian and international curricula.' },
  ];

  const features = [
    { icon: Heart, title: 'Child-Centered Learning', description: 'Every child learns at their own pace in a supportive environment.' },
    { icon: BookOpen, title: 'Montessori Method', description: 'Hands-on learning materials that engage all senses.' },
    { icon: Users, title: 'Small Class Sizes', description: 'Personalized attention with low student-to-teacher ratios.' },
    { icon: Sparkles, title: 'Holistic Development', description: 'Focus on academic, social, emotional, and physical growth.' },
  ];

  return (
    <>
      <PageHeader
        title={school.name}
        subtitle={school.tagline}
        breadcrumbs={[
          { label: 'Our Schools', href: '/our-schools' },
          { label: 'Montessori & Primary' },
        ]}
        backgroundImage="https://images.unsplash.com/photo-1587654780291-39c9404d746b?w=1600&h=400&fit=crop"
      />

      {/* Introduction */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <span className="inline-block px-4 py-1 bg-emerald-100 text-emerald-700 rounded-full text-sm font-medium mb-4">
                {school.ageRange}
              </span>
              <h2 className="text-3xl md:text-4xl font-display font-bold text-gray-900 mb-6">
                Where Learning Begins with Wonder
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed mb-6">
                At Stirret Montessori Nursery & Primary School, we believe every child is naturally
                curious and eager to learn. Our Montessori-inspired approach nurtures this innate
                curiosity while building strong academic foundations.
              </p>
              <p className="text-gray-600 leading-relaxed mb-8">
                We create an environment where children feel safe to explore, discover, and grow at
                their own pace, developing the confidence and skills they need for lifelong success.
              </p>
              <Link to="/montessori/curriculum" className="btn-primary group inline-flex">
                Explore Our Curriculum
                <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={20} />
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <img
                src="https://images.unsplash.com/photo-1503676260728-1c00da094a0b?w=600&h=500&fit=crop"
                alt="Children learning"
                className="w-full h-[500px] object-cover rounded-2xl shadow-hard"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Programs */}
      <section className="section-padding bg-gray-50">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-display font-bold text-gray-900 mb-4">
              Our Programs
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Age-appropriate programs designed to meet each child's developmental needs.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {programs.map((program, index) => (
              <motion.div
                key={program.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white p-6 rounded-2xl shadow-soft hover:shadow-hard transition-shadow"
              >
                <span className="inline-block px-3 py-1 bg-emerald-100 text-emerald-700 rounded-full text-xs font-medium mb-3">
                  {program.ages}
                </span>
                <h3 className="text-xl font-bold text-gray-900 mb-2">{program.name}</h3>
                <p className="text-gray-600 text-sm">{program.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="section-padding bg-emerald-700">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-display font-bold text-white mb-4">
              The Stirret Montessori Difference
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="text-center"
                >
                  <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                    <Icon className="text-white" size={32} />
                  </div>
                  <h3 className="text-xl font-bold text-white mb-2">{feature.title}</h3>
                  <p className="text-emerald-100">{feature.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-white">
        <div className="container-custom text-center">
          <h2 className="text-3xl md:text-4xl font-display font-bold text-gray-900 mb-4">
            Ready to Visit Us?
          </h2>
          <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
            Schedule a tour to see our learning environment and meet our dedicated team.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/montessori/contact" className="btn-primary">
              Schedule a Tour
            </Link>
            <Link to="/admissions" className="btn-secondary">
              Apply Now
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default MontessoriOverview;
